import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  Typography,
  Paper,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import useDelete from "../../../../api/useDelete";
import { useTranslation } from "react-i18next";
import EditTestResult from "./EditTestResult";

const ListUltrasoundResult = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [openDialog, setOpenDialog] = useState(null);

  const [editData, setEditData] = useState("");
  const imageRef = useRef();

  const { data, isLoading, isError, error, refetch } = useFetchObjects(
    ["ultrasound-results"],
    `ultrasound-results/?search=${searchTerm}`,
    token,
  );
  const { handleDelete, ConfirmDialog } = useDelete(
    "ultrasound-results",
    token,
  );

  useEffect(() => {
    refetch();
  }, [searchTerm]);

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    const imgElement = imageRef.current;
    if (imgElement && imgElement.requestFullscreen) {
      imgElement.requestFullscreen();
    } else if (imgElement && imgElement.webkitRequestFullscreen) {
      imgElement.webkitRequestFullscreen();
    } else if (imgElement && imgElement.msRequestFullscreen) {
      imgElement.msRequestFullscreen();
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(!openDialog);
  };

  const handleEdit = (result) => {
    handleOpenDialog();
    setEditData(result);
  };
  const tableStyle = {
    textAlign: "center",
    fontWeight: "bold",
  };
  const tableCellStyle = {
    textAlign: "center",
  };

  return (
    <Box>
      {/* Search Bar */}
      <Box mb={4}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          {t("ultrasound.ultrasoundResults")}
        </Typography>
        <TextField
          label={t("ultrasound.searchPlaceholder")}
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: "100%", mt: 2 }}
        />
      </Box>

      <Divider sx={{ marginY: 3 }} />

      {/* Loading State */}
      {isLoading && (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {isError && (
        <Box mt={4}>
          <Alert severity="error">
            {t("ultrasound.error")}: {error.message}
          </Alert>
        </Box>
      )}

      {/* Ultrasound Result List */}
      {data && data.length > 0 && (
        <TableContainer
          component={Paper}
          sx={{ marginTop: 3, boxShadow: 3, borderRadius: 2 }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell style={tableStyle}>
                  {t("ultrasound.patientName")}
                </TableCell>
                <TableCell style={tableStyle}>
                  {t("ultrasound.ultrasoundType")}
                </TableCell>
                <TableCell style={tableStyle}>
                  {t("ultrasound.findings")}
                </TableCell>
                <TableCell style={tableStyle}>
                  {t("ultrasound.resultDate")}
                </TableCell>
                <TableCell style={tableStyle}>
                  {t("ultrasound.image")}
                </TableCell>
                <TableCell style={tableStyle}>
                  {t("ultrasound.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((result) => (
                <TableRow
                  key={result.id}
                  hover
                  sx={{
                    transition: "0.3s",
                    "&:hover": { backgroundColor: "#f0f0f0" },
                  }}
                >
                  <TableCell style={tableCellStyle}>
                    {result.patient_display}
                  </TableCell>
                  <TableCell style={tableCellStyle}>
                    {result.ultrasound_type_display}
                  </TableCell>
                  <TableCell style={tableCellStyle}>
                    {result.findings || t("ultrasound.noFinding")}
                  </TableCell>
                  <TableCell style={tableCellStyle}>
                    {new Date(result.result_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell style={tableCellStyle}>
                    {result.image ? (
                      <img
                        src={result.image}
                        alt="Ultrasound"
                        width="50"
                        height="50"
                        style={{ cursor: "pointer", borderRadius: "5px" }}
                        onClick={() => handleImageClick(result.image)}
                        ref={imageRef}
                      />
                    ) : (
                      t("ultrasound.noImage")
                    )}
                  </TableCell>
                  <TableCell style={tableCellStyle}>
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(result)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(result.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!isLoading && data?.length === 0 && (
        <Box mt={4}>
          <Alert severity="info">{t("ultrasound.noUltResult")}</Alert>
        </Box>
      )}
      <ConfirmDialog />

      <EditTestResult
        editData={editData}
        openDialog={openDialog}
        handleOpenDialog={handleOpenDialog}
      />
    </Box>
  );
};

export default ListUltrasoundResult;
